import React from 'react';
import { ArrowRight, LogOut } from 'lucide-react';
import { CleroS } from './Logo';

interface WebOrganizerBlockedTakeoverProps {
    sessionToken: string;
    appDeepLink?: string;
    sessionId?: string;
    merchantName?: string;
    amount?: number;
    itemsCount?: number;
    participantsCount?: number;
    createdDate?: string;
    groupName?: string;
    onLogout?: () => void;
    loggingOut?: boolean;
}

export const WebOrganizerBlockedTakeover: React.FC<WebOrganizerBlockedTakeoverProps> = ({
    sessionToken,
    appDeepLink,
    sessionId,
    onLogout,
    loggingOut = false,
}) => {
    const targetUrl = appDeepLink || `clero://pay/${sessionToken || sessionId || ''}`;

    return (
        <div className="fixed inset-0 z-[100] min-h-[100dvh] w-full bg-slate-950/95 backdrop-blur-md text-white flex flex-col justify-between p-6 animate-in fade-in duration-200">
            {/* Top Bar */}
            <div className="flex items-center justify-end max-w-sm w-full mx-auto">
                {onLogout && (
                    <button
                        type="button"
                        onClick={onLogout}
                        disabled={loggingOut}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-xs font-semibold text-slate-300 hover:text-white transition-colors disabled:opacity-50"
                    >
                        <LogOut className="w-3.5 h-3.5" />
                        <span>{loggingOut ? 'Signing out...' : 'Sign out'}</span>
                    </button>
                )}
            </div>

            {/* Center Content */}
            <div className="flex flex-col items-center text-center max-w-xs mx-auto space-y-6 my-auto">
                <div className="w-20 h-20 rounded-3xl bg-teal-600 flex items-center justify-center shadow-2xl shadow-teal-500/30 ring-1 ring-white/20">
                    <CleroS size={44} color="#FFFFFF" />
                </div>

                <div className="space-y-2">
                    <h2 className="text-2xl font-black tracking-tight text-white">
                        Open in Clero
                    </h2>
                    <p className="text-sm font-medium text-slate-300 leading-relaxed">
                        You created this request. Open the Clero app to track claims, adjust shares, and collect payback.
                    </p>
                </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex flex-col gap-3 max-w-sm w-full mx-auto pb-4">
                <a
                    href={targetUrl}
                    className="w-full py-4 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-extrabold text-base flex items-center justify-center gap-2 transition-transform active:scale-[0.98] shadow-lg shadow-teal-900/30 text-center"
                >
                    <span>Open in Clero</span>
                    <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                </a>

                {onLogout ? (
                    <button
                        type="button"
                        onClick={onLogout}
                        disabled={loggingOut}
                        className="w-full py-3 text-center text-xs font-bold text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                    >
                        {loggingOut ? 'Signing out...' : 'Not you? Sign in with another number'}
                    </button>
                ) : (
                    <a
                        href="https://apps.apple.com/app/clero/id6739989803"
                        target="_blank"
                        rel="noreferrer"
                        className="w-full py-2.5 text-center text-xs font-semibold text-slate-400 hover:text-slate-200 transition-colors"
                    >
                        Download on the App Store
                    </a>
                )}
            </div>
        </div>
    );
};

export default WebOrganizerBlockedTakeover;
